/**
 * NextFixtureCountdownWidget (Next Match)
 *
 * Shows the next upcoming match with a live countdown timer
 * and a quick "Select Squad" shortcut button.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Clock, MapPin, Trophy, Users, ArrowRight, Timer, Shield } from 'lucide-react';
import { resolveBadgeUrl } from './utils';

const formatDateFull = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
};

const getCountdown = (dateString, timeString) => {
  if (!dateString) return null;
  const now = new Date();
  const matchDateTime = new Date(`${dateString}T${timeString || '15:00'}:00`);
  const diff = matchDateTime - now;

  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, passed: true };

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, passed: false };
};

function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center flex-1" data-testid={`countdown-${label.toLowerCase()}`}>
      <div className="w-full max-w-[72px] aspect-square rounded-xl bg-gray-900 text-white flex items-center justify-center text-2xl sm:text-3xl font-bold tabular-nums shadow-sm">
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-[10px] sm:text-xs text-gray-500 mt-1.5 uppercase tracking-wider font-medium">{label}</span>
    </div>
  );
}

export const NextFixtureCountdownWidget = ({ stats, user, opponentBadgeMap = {} }) => {
  const nextMatch = stats?.upcoming_matches?.[0];
  const [countdown, setCountdown] = useState(null);

  const countdownEnabled = user?.countdown_timer_enabled !== false;
  const showOpponentBadges = user?.show_opponent_badges !== false;
  const activeMatchDayId = localStorage.getItem('matchday_active_match_id');

  useEffect(() => {
    if (!nextMatch) return;
    const update = () => setCountdown(getCountdown(nextMatch.date, nextMatch.kick_off_time));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [nextMatch?.date, nextMatch?.kick_off_time]);

  if (!nextMatch) {
    return (
      <div className="h-full" data-testid="next-fixture-countdown">
        <div className="flex flex-row items-center justify-between pb-4">
          <h3 className="text-xl font-semibold font-title">Next Match</h3>
        </div>
        <div className="bg-white rounded-2xl p-4" style={{ border: '1px solid #e5e2dc' }}>
          <div className="text-center py-8 text-gray-400">
            <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
            <p>No upcoming fixtures</p>
          </div>
        </div>
      </div>
    );
  }

  const venueLabel = nextMatch.venue === 'home' ? 'Home' : nextMatch.venue === 'away' ? 'Away' : 'Neutral';
  const opponentBadge = resolveBadgeUrl(opponentBadgeMap[(nextMatch.opponent || '').toLowerCase().trim()]);

  // Check if match is today
  const isMatchDay = nextMatch.date === new Date().toISOString().split('T')[0] || (countdown && countdown.passed && countdown.days === 0);

  return (
    <div className="h-full" data-testid="next-fixture-countdown">
      <div className="flex flex-row items-center justify-between pb-4">
        <h3 className="text-xl font-semibold font-title">Next Match</h3>
      </div>
      <div className="bg-white rounded-2xl p-5 flex flex-col h-[calc(100%-44px)]" style={{ border: '1px solid #e5e2dc' }}>
        {/* Header row: name + date/time on left, badge on right */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight truncate" data-testid="next-match-opponent">
              {nextMatch.opponent}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {formatDateFull(nextMatch.date)}
              {nextMatch.kick_off_time && (
                <span className="text-gray-400"> at {nextMatch.kick_off_time}</span>
              )}
            </p>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2.5 text-sm text-gray-500">
              {nextMatch.competition && (
                <span className="flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
                  {nextMatch.competition}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
                {venueLabel}
              </span>
            </div>
          </div>
          {showOpponentBadges && (
            opponentBadge ? (
              <img
                src={opponentBadge}
                alt={`${nextMatch.opponent} badge`}
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain shrink-0"
                data-testid="next-match-opponent-badge"
              />
            ) : (
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0"
                data-testid="next-match-opponent-badge"
              >
                <Shield className="w-8 h-8 text-gray-300" strokeWidth={1.5} />
              </div>
            )
          )}
        </div>

        {/* Countdown */}
        {countdownEnabled && countdown && !countdown.passed && (
          <div className="flex items-center justify-between gap-1 sm:gap-2 py-4 px-2 sm:px-3 mt-4 bg-gray-50 rounded-xl" data-testid="countdown-timer">
            <CountdownUnit value={countdown.days} label="Days" />
            <span className="text-gray-300 text-lg font-light mt-[-18px]">:</span>
            <CountdownUnit value={countdown.hours} label="Hrs" />
            <span className="text-gray-300 text-lg font-light mt-[-18px]">:</span>
            <CountdownUnit value={countdown.minutes} label="Min" />
            <span className="text-gray-300 text-lg font-light mt-[-18px]">:</span>
            <CountdownUnit value={countdown.seconds} label="Sec" />
          </div>
        )}

        {countdownEnabled && countdown?.passed && (
          <div className="flex items-center justify-center py-3 mt-4 bg-emerald-50 rounded-xl" data-testid="matchday-banner">
            <span className="text-sm font-bold text-emerald-700 uppercase tracking-wider">Match Day!</span>
          </div>
        )}

        {/* Select Squad / Match Day Mode / Return to Match Day */}
        <div className="mt-auto pt-4">
          {activeMatchDayId ? (
            <Link to={`/matches/${activeMatchDayId}/matchday`} data-testid="return-matchday-shortcut">
              <Button
                className="w-full rounded-xl font-semibold gap-2 shadow-sm bg-red-600 hover:bg-red-700 text-white"
                data-testid="return-matchday-btn"
              >
                <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                Return to Match Day
                <ArrowRight className="w-4 h-4 ml-auto" strokeWidth={1.5} />
              </Button>
            </Link>
          ) : isMatchDay && user?.match_day_mode_enabled !== false ? (
            <Link to={`/matches/${nextMatch.id}/matchday`} data-testid="matchday-mode-shortcut">
              <Button
                className="w-full rounded-xl font-semibold gap-2 shadow-sm bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="matchday-mode-btn"
              >
                <Timer className="w-4 h-4" strokeWidth={1.5} />
                Match Day Mode
                <ArrowRight className="w-4 h-4 ml-auto" strokeWidth={1.5} />
              </Button>
            </Link>
          ) : (
            <Link to={`/matches/${nextMatch.id}/edit?tab=squad`} data-testid="select-squad-shortcut">
              <Button
                className="w-full rounded-xl font-semibold gap-2 shadow-sm"
                style={{ backgroundColor: user?.button_color || '#064E3B' }}
                data-testid="select-squad-btn"
              >
                <Users className="w-4 h-4" strokeWidth={1.5} />
                Select Squad
                <ArrowRight className="w-4 h-4 ml-auto" strokeWidth={1.5} />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default NextFixtureCountdownWidget;
